import type { ForwardedRef } from "react";
import React, { forwardRef, useEffect, useRef } from "react";
import type { KivraCSSObject } from "../../theme/styled";
import { styled } from "../../theme/styled";
import { isPromise } from "../../util/isPromise";
import { mergeRefs } from "../../util/mergeRefs/mergeRefs";
import { useStateIfMounted } from "../../util/useStateIfMounted/useStateIfMounted";
import { Loader } from "../loader/Loader";
import {
  BaseStyles,
  DisabledStyles,
  DismissiveStyles,
  FilledStyles,
  LinkStyles,
  PrimaryStyles,
  SecondaryStyles,
} from "./Button.styles";
import { Icon } from "./components/Icon";
import { ButtonContext } from "./context";
import { hasIconOnly } from "./util";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "filled"
  | "link"
  | "dismissive";

export interface ButtonProps
  extends Omit<React.ComponentProps<"button">, "children"> {
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => unknown;
  onSubmit?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => unknown;
  /** The size of the button. `small` by default */
  size?: "small" | "medium";
  /** If true, button will be using full width of container. false by default */
  fullWidth?: boolean;
  /** Button type. `primary` by default */
  variant?: ButtonVariant;
  /**
   * When true, the button shows a spinner instead of text.
   * onClick is also disabled when true
   */
  progress?: boolean;
  /** Target button in tests */
  dataTestId?: string;
  /** Target component type in tests */
  dataComponentType?: string;
  /** One or many children */
  children: React.ReactNode | React.ReactNode[];
}

const ButtonComponent = (
  {
    dataComponentType,
    dataTestId,
    variant = "primary",
    size = "small",
    progress,
    onClick,
    onSubmit,
    children,
    ...props
  }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
): React.JSX.Element => {
  const [showSpinner, setShowSpinner] = useStateIfMounted(false);

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setShowSpinner(Boolean(progress));
  }, [progress]);

  const handleAction =
    (action: "onClick" | "onSubmit") =>
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (showSpinner) {
        return;
      }

      const result = { onClick, onSubmit }[action]?.(event);
      if (isPromise(result)) {
        setShowSpinner(true);
        void result.finally(() => setShowSpinner(Boolean(progress)));
      }
    };

  const iconOnly = hasIconOnly(children);

  return (
    <ButtonContext.Provider value={{ size: size }}>
      <ButtonStyled
        data-component-type={dataComponentType || "Button"}
        data-test-id={dataTestId}
        ref={mergeRefs([buttonRef, ref])}
        onClick={handleAction("onClick")}
        onSubmit={handleAction("onSubmit")}
        type={props.type ?? "button"}
        variant={variant}
        size={size}
        iconOnly={iconOnly}
        {...props}
      >
        {showSpinner && <StyledSpinner size={size} color="currentColor" />}
        <StyledChildren visible={!showSpinner}>{children}</StyledChildren>
      </ButtonStyled>
    </ButtonContext.Provider>
  );
};

export const getStyles = (variant: ButtonProps["variant"]): KivraCSSObject => {
  switch (variant) {
    case "secondary":
      return SecondaryStyles;
    case "filled":
      return FilledStyles;
    case "link":
      return LinkStyles;
    case "dismissive":
      return DismissiveStyles;
    default:
      return PrimaryStyles;
  }
};

const ButtonStyled = styled.button<ButtonProps & { iconOnly: boolean }>(
  BaseStyles,
  ({ variant }: ButtonProps) => getStyles(variant),
  ({ disabled }: ButtonProps) => (disabled ? DisabledStyles : {}),
);

const StyledSpinner = styled(Loader)({
  position: "absolute",
  display: "inline-flex",
});

const StyledChildren = styled("span")<{ visible: boolean }>((p) => ({
  display: "grid",
  gridColumnGap: "$spacing-8",
  gridAutoFlow: "column",
  alignItems: "center",
  visibility: p.visible ? "visible" : "hidden",
}));

export const Button = Object.assign(forwardRef(ButtonComponent), { Icon });
